import React, { useState, useEffect, useRef } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { NormalizedDogWalk, ReviewEntry } from '../types';
import { renderStars } from '../utils/renderStars';

const dogWalkIcon = new L.Icon({
    iconUrl: '/green-paw-marker.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
});

interface DogWalkMarkerProps {
    walk: NormalizedDogWalk;
    onSelect: (walk: NormalizedDogWalk) => void;
    onShowTrail: (walk: NormalizedDogWalk) => void;
    onAddReview: (walk: NormalizedDogWalk) => void;
    isSelected?: boolean;
}

// This interface should match what is saved in the backend and compatible with ReviewEntry
interface DoggleReview extends ReviewEntry {
    stars?: number;
    siteID?: string;
}

const DogWalkMarker: React.FC<DogWalkMarkerProps> = ({ walk, onSelect, onShowTrail, onAddReview, isSelected = false }) => {
    const [reviews, setReviews] = useState<DoggleReview[]>([]);
    const [reviewsLoaded, setReviewsLoaded] = useState(false);
    const markerRef = useRef<L.Marker>(null);

    const lat = walk.location.lat;
    const lng = walk.location.lng;

    // Apply highlighting class to marker icon when selected
    useEffect(() => {
        if (markerRef.current) {
            const markerElement = markerRef.current.getElement();
            if (markerElement) {
                if (isSelected) {
                    markerElement.classList.add('highlighted-marker');
                } else {
                    markerElement.classList.remove('highlighted-marker');
                }
            }
        }
    }, [isSelected]);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.warn("Skipping invalid dog walk marker:", walk);
        return null;
    }

    const handleMarkerClick = async () => {
        let currentReviews = reviews;

        if (!reviewsLoaded) {
            try {
                const response = await fetch('/api/doggle-reviews');
                const allReviews: DoggleReview[] = await response.json();
                // Fix: safely access r.review using optional chaining to prevent crash
                const walkReviews = allReviews.filter(r => String(r?.review?.siteID) === String(walk.id) || String(r?.siteID) === String(walk.id));
                currentReviews = walkReviews;
                setReviews(currentReviews);
                setReviewsLoaded(true);
            } catch (error) {
                console.error('Error fetching reviews:', error);
                setReviewsLoaded(true);
            }
        }

        onShowTrail(walk); // Show GPX trail
        onSelect({ ...walk, reviews: currentReviews }); // Show details in sidebar
    };

    return (
        <Marker
            ref={markerRef}
            position={[lat, lng]}
            icon={dogWalkIcon}
            eventHandlers={{
                click: handleMarkerClick
            }}
        />
    );
};

export default DogWalkMarker;
